import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Divider, Table, TableHead, TableRow, TableCell, TableBody, IconButton, DialogTitle, DialogContent, DialogActions, TextField, Tooltip, Snackbar, Alert } from "@mui/material";
import AppDialog from "../../../common/AppDialog";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import jsPDF from "jspdf";
import logo from "../../../assets/images/hashstack-logo-orange-black.png";
import { getData, putData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

function SalaryStructureView({ data, onBack }) {
    const { selectedEmployeeData, employee } = data || {};

    const [editOpen, setEditOpen] = useState(false);
    const [editedNotes, setEditedNotes] = useState([]);
    const [noteId, setNoteId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    //  Fetch shared notes for all employees
    useEffect(() => {
        const fetchNotes = async () => {
            try {
                const res = await getData(API_ENDPOINTS.getNotes);
                if (res?.status === "SUCCESS" && Array.isArray(res?.result)) {
                    const firstSet = res.result[0];
                    if (firstSet?.note && Array.isArray(firstSet.note)) {
                        setEditedNotes(firstSet.note);
                        setNoteId(firstSet.id);
                    } else setEditedNotes([]);
                } else {
                    setEditedNotes([]);
                }
            } catch (error) {
                setEditedNotes([]);
            }
        };
        fetchNotes();
    }, []);

    if (!selectedEmployeeData) return null;

    //  Currency formatter — always two decimals
    const fmt = (v) =>
        `₹${Number(v || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;

    //  Grouped Sections for User-Friendliness (filtered for non-zero values)
    const sections = [
        {
            title: "Earnings (A)",
            rows: [
                { label: "Basic Pay", monthly: selectedEmployeeData?.basicPayMonthly, annual: selectedEmployeeData?.basicPayAnnual },
                { label: "HRA", monthly: selectedEmployeeData?.hraMonthly, annual: selectedEmployeeData?.hraAnnual },
                { label: "Conveyance Allowance", monthly: selectedEmployeeData?.conveyanceAllowanceMonthly, annual: selectedEmployeeData?.conveyanceAllowanceAnnual },
                { label: "Special Allowance", monthly: selectedEmployeeData?.specialAllowanceMonthly, annual: selectedEmployeeData?.specialAllowanceAnnual },
                ...(selectedEmployeeData?.otherAllowancesDetail
                    ? Object.entries(selectedEmployeeData.otherAllowancesDetail).map(([type, amount]) => ({
                        label: type,
                        monthly: amount,
                        annual: Number(amount) * 12,
                    }))
                    : []),
                { label: "Total Gross Salary (A)", monthly: selectedEmployeeData?.grossEarningsMonthly, annual: selectedEmployeeData?.grossEarningsAnnual, isTotal: true },
            ],
        },
        {
            title: "Employee Deductions (B)",
            rows: [
                { label: "PF (Employee Share)", monthly: selectedEmployeeData?.pfEmployeeMonthly, annual: selectedEmployeeData?.pfEmployeeAnnual },
                { label: "ESI (Employee Share)", monthly: selectedEmployeeData?.esiEmployeeMonthly, annual: selectedEmployeeData?.esiEmployeeAnnual },
                { label: "Professional Tax (PT)", monthly: selectedEmployeeData?.professionalTaxMonthly, annual: selectedEmployeeData?.professionalTaxAnnual },
                { label: "Income Tax (TDS)", monthly: Number(selectedEmployeeData?.monthlyTDS || 0) - Number(selectedEmployeeData?.monthlyCess || 0) - Number(selectedEmployeeData?.monthlySurcharge || 0), annual: Number(selectedEmployeeData?.annualTDS || 0) - Number(selectedEmployeeData?.annualCess || 0) - Number(selectedEmployeeData?.annualSurcharge || 0) },
                { label: "Surcharge", monthly: selectedEmployeeData?.monthlySurcharge, annual: selectedEmployeeData?.annualSurcharge },
                { label: "Education Cess (4%)", monthly: selectedEmployeeData?.monthlyCess, annual: selectedEmployeeData?.annualCess },
                { label: "Marginal Relief (Relief)", monthly: -Number(selectedEmployeeData?.marginalRelief || 0) / 12, annual: -Number(selectedEmployeeData?.marginalRelief || 0) },
                { label: "Total Deductions (B)", monthly: selectedEmployeeData?.totalDeductionsMonthly, annual: selectedEmployeeData?.totalDeductionsAnnual, isTotal: true },
            ],
        },
        {
            title: "Employer Contributions (C)",
            rows: [
                { label: "PF (Employer Share)", monthly: selectedEmployeeData?.pfEmployerMonthly, annual: selectedEmployeeData?.pfEmployerAnnual },
                { label: "ESI (Employer Share)", monthly: selectedEmployeeData?.employerContributions?.esi || selectedEmployeeData?.esiEmployerMonthly || 0, annual: selectedEmployeeData?.esiEmployerAnnual },
                { label: "Gratuity Accrual", monthly: selectedEmployeeData?.gratuityAccrualMonthly, annual: selectedEmployeeData?.gratuityAccrualAnnual },
                { label: "Bonus / Performance Accrual", monthly: null, annual: selectedEmployeeData?.performanceBonusAnnual || 0 },
            ],
        },
        {
            title: "Summary (Net & CTC)",
            rows: [
                { label: "Monthly Net Take Home (A - B)", monthly: selectedEmployeeData?.netSalaryMonthly, annual: selectedEmployeeData?.netSalaryAnnual, isTotal: true },
                { label: "Variable Pay (Annual)", monthly: null, annual: selectedEmployeeData?.variablePay },
                { label: "Annual CTC (A + C + Variable)", monthly: null, annual: selectedEmployeeData?.annualCTC, isTotal: true, isCTC: true },
            ],
        },
    ].map(section => ({
        ...section,
        rows: section.rows.filter(row =>
            parseFloat(row.monthly || 0) !== 0 ||
            parseFloat(row.annual || 0) !== 0 ||
            ["Surcharge", "Marginal Relief (Relief)", "Income Tax (TDS)", "Education Cess (4%)"].includes(row.label)
        )
    })).filter(section => section.rows.length > 0);

    //  Save Notes API
    const handleSaveNotes = async () => {
        if (!noteId) {
            setSnackbar({
                open: true,
                message: "No note record found to update!",
                severity: "error",
            });
            return;
        }

        setSaving(true);
        try {
            const payload = { id: noteId, note: editedNotes.map((n) => n.trim()).filter(Boolean) };
            const res = await putData(API_ENDPOINTS.updateNote, payload);

            if (res?.status === "SUCCESS" || res?.statusCode === 200) {
                setSnackbar({
                    open: true,
                    message: "Notes updated successfully!",
                    severity: "success",
                });
                setEditOpen(false);
            } else throw new Error(res?.message || "Failed to update notes");
        } catch (error) {
            setSnackbar({
                open: true,
                message: "Error updating notes!",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    //  PDF Download (two decimals included)
    const handleDownload = () => {
        const img = new window.Image();
        img.src = logo;
        img.onload = () => {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 16;
            const startY = 20;

            doc.addImage(img, "PNG", margin, startY, 25, 25);
            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            doc.text("Salary Structure", pageWidth / 2, startY + 10, { align: "center" });

            doc.setFontSize(12);
            doc.setFont("helvetica", "normal");
            doc.text(
                `Employee: ${employee?.first_name || ""} ${employee?.last_name || ""} (${employee?.emp_id || ""})`,
                pageWidth / 2,
                startY + 18,
                { align: "center" }
            );
            doc.setFontSize(10);
            doc.text(
                `Type: ${selectedEmployeeData.employmentType || (selectedEmployeeData.isPartTime ? "Part-time" : "Full-time")}${selectedEmployeeData.isPartTime ? ` (${selectedEmployeeData.workingHoursPerDay} hrs/day)` : ""}`,
                pageWidth / 2,
                startY + 24,
                { align: "center" }
            );
            const startTableY = startY + 40;
            const colWidths = [90, 45, 45];
            const headers = ["Component", "Monthly (Rs.)", "Annual (Rs.)"];
            let currentY = startTableY;
            let currentX = margin;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            headers.forEach((header, i) => {
                doc.rect(currentX, currentY, colWidths[i], 10);
                doc.text(header, currentX + 3, currentY + 7);
                currentX += colWidths[i];
            });
            currentY += 10;

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            sections.forEach((section) => {
                // Section Header
                currentX = margin;
                doc.setFont("helvetica", "bold");
                doc.setFillColor(240, 240, 240);
                doc.rect(currentX, currentY, colWidths.reduce((a, b) => a + b, 0), 10, "F");
                doc.text(section.title, currentX + 3, currentY + 7);
                currentY += 10;

                // Section Rows
                doc.setFont("helvetica", "normal");
                section.rows.forEach((row) => {
                    currentX = margin;
                    const rowHeight = 9;
                    const isVariablePay = row.label.toLowerCase().includes("variable pay");
                    const isAnnualOnly = row.label.toLowerCase().includes("annual ctc") || isVariablePay;

                    if (row.isTotal) {
                        doc.setFont("helvetica", "bold");
                        if (row.isCTC) doc.setFillColor(232, 245, 233); // light green for CTC
                        else doc.setFillColor(250, 250, 250);
                        doc.rect(currentX, currentY, colWidths.reduce((a, b) => a + b, 0), rowHeight, "F");
                    } else {
                        doc.setFont("helvetica", "normal");
                    }

                    const monthlyVal = isAnnualOnly
                        ? "-"
                        : row.monthly
                            ? `Rs. ${Number(row.monthly).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : "-";

                    const annualVal =
                        row.annual && Number(row.annual) !== 0
                            ? `Rs. ${Number(row.annual).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : "-";

                    doc.rect(currentX, currentY, colWidths[0], rowHeight);
                    doc.text(row.label, currentX + 3, currentY + 6);
                    currentX += colWidths[0];
                    doc.rect(currentX, currentY, colWidths[1], rowHeight);
                    doc.text(monthlyVal, currentX + 3, currentY + 6);
                    currentX += colWidths[1];
                    doc.rect(currentX, currentY, colWidths[2], rowHeight);
                    doc.text(annualVal, currentX + 3, currentY + 6);
                    currentY += rowHeight;
                });
                currentY += 2; // small gap between sections
            });

            //  Notes Section
            const noteStartY = currentY + 8;
            const boxX = margin;
            const boxWidth = colWidths.reduce((a, b) => a + b, 0);
            const lineSpacing = 6;
            doc.setDrawColor(180);
            doc.setFillColor(245, 245, 245);
            const splitNotes = editedNotes.map((note, index) => {
                const prefix = `${index + 1}. `;
                return doc.splitTextToSize(prefix + note, boxWidth - 10);
            });
            const boxHeight = splitNotes.reduce(
                (acc, lines) => acc + doc.getTextDimensions(lines).h + lineSpacing,
                10
            );
            doc.rect(boxX, noteStartY, boxWidth, boxHeight, "FD");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text("Note:", boxX + 3, noteStartY + 6);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            let textY = noteStartY + 12;
            splitNotes.forEach((line) => {
                doc.text(line, boxX + 6, textY);
                textY += doc.getTextDimensions(line).h + lineSpacing;
            });
            doc.save(`Salary_Structure_${employee?.first_name || "Employee"}.pdf`);
        };
    };

    //  Render
    return (
        <Box sx={{ p: 3, maxWidth: 900, margin: "auto", bgcolor: "#fcfcfc", borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Button variant="outlined" onClick={onBack} sx={{ textTransform: "none" }}>
                    Back
                </Button>
                <Typography variant="h5" sx={{ flex: 1, textAlign: "center", color: "#1565c0", fontWeight: 600 }}>
                    Salary Structure Preview
                </Typography>
                <IconButton color="primary" onClick={handleDownload}>
                    <DownloadIcon />
                </IconButton>
            </Box>

            {/* Employee Info */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {employee?.first_name} {employee?.last_name} ({employee?.emp_id})
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
                Employment Type: {selectedEmployeeData.employmentType || (selectedEmployeeData.isPartTime ? "Part-time" : "Full-time")}
            </Typography>
            {selectedEmployeeData.isPartTime && (
                <Typography sx={{ mb: 1 }}>
                    Working Hours: {selectedEmployeeData.workingHoursPerDay} hrs/day
                </Typography>
            )}
            <Typography sx={{ mb: 1 }}>
                Gross Salary(PM): {fmt(selectedEmployeeData.grossEarningsMonthly)}
            </Typography>
            <Divider sx={{ my: 2 }} />

            {/* Salary Table */}
            <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: "#0d47a1" }}>Component</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#0d47a1" }}>Monthly (Rs.)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#0d47a1" }}>Annual (Rs.)</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sections.map((section, sIdx) => (
                        <React.Fragment key={sIdx}>
                            {/* Section Header */}
                            <TableRow>
                                <TableCell colSpan={3} sx={{ bgcolor: "#f0f4f8", fontWeight: 700, color: "#1a237e" }}>
                                    {section.title}
                                </TableCell>
                            </TableRow>
                            {/* Section Rows */}
                            {section.rows.map((row, rIdx) => {
                                const isAnnualOnly = row.label.toLowerCase().includes("annual ctc") || row.label.toLowerCase().includes("variable pay");
                                return (
                                    <TableRow key={rIdx} sx={row.isTotal ? { bgcolor: row.isCTC ? "#e8f5e9" : "#fafafa", "& td": { fontWeight: 700 } } : {}}>
                                        <TableCell sx={{ pl: 3 }}>{row.label}</TableCell>
                                        <TableCell align="right">{isAnnualOnly ? "-" : fmt(row.monthly)}</TableCell>
                                        <TableCell align="right">{fmt(row.annual)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />

            {/* Notes Section */}
            <Box sx={{ mt: 3, border: "1px solid #ccc", borderRadius: 1, p: 1.5, bgcolor: "#fafafa" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Note:</Typography>
                    <IconButton size="small" color="primary" onClick={() => setEditOpen(true)} sx={{ ml: 1 }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Box>
                {editedNotes?.map((note, idx) => (
                    <Typography key={idx} variant="body2" sx={{ mb: 0.3 }}>
                        {`${idx + 1}. ${note}`}
                    </Typography>
                ))}
            </Box>

            {/* Edit Notes Dialog */}
            <AppDialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Notes</DialogTitle>
                <DialogContent dividers>
                    {editedNotes.map((note, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <TextField
                                label={`Note ${idx + 1}`}
                                fullWidth
                                size="small"
                                value={note}
                                onChange={(e) => {
                                    const newNotes = [...editedNotes];
                                    newNotes[idx] = e.target.value;
                                    setEditedNotes(newNotes);
                                }}
                            />
                            <Tooltip title="Delete this note">
                                <IconButton
                                    color="error"
                                    onClick={() => {
                                        const updated = editedNotes.filter((_, i) => i !== idx);
                                        setEditedNotes(updated);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                    <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                            startIcon={<AddCircleOutlineIcon />}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ textTransform: "none" }}
                            onClick={() => setEditedNotes((prev) => [...prev, ""])}
                        >
                            Add Note
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={saving} onClick={handleSaveNotes} sx={{ textTransform: "none" }}>
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </AppDialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SalaryStructureView;